import { extname } from 'path';

export const MAX_DOCUMENT_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGE_UPLOAD_SIZE_BYTES = 2 * 1024 * 1024;
export const MAX_TOTAL_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;
export const MAX_IMAGE_DIMENSION_PX = 1600;
export const IMAGE_JPEG_QUALITY = 75;

export const DOCUMENT_UPLOAD_EXTENSIONS = ['.html', '.htm', '.md', '.markdown'] as const;
export const IMAGE_UPLOAD_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif'] as const;
export const ALLOWED_UPLOAD_EXTENSIONS = [
    ...DOCUMENT_UPLOAD_EXTENSIONS,
    ...IMAGE_UPLOAD_EXTENSIONS,
] as const;

export type UploadContentType = 'text/html' | 'text/markdown' | 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';
export type UploadValidationCode = 'UNSUPPORTED_FILE_TYPE' | 'FILE_TOO_LARGE' | 'IMAGE_TOO_LARGE';

export interface UploadValidationError {
    code: UploadValidationCode;
    message: string;
}

export function formatUploadSize(bytes: number): string {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export function getUploadFileExtension(filename: string): string {
    return extname(filename).toLowerCase();
}

export function isImageUploadExtension(filename: string): boolean {
    const ext = getUploadFileExtension(filename);
    return IMAGE_UPLOAD_EXTENSIONS.includes(ext as typeof IMAGE_UPLOAD_EXTENSIONS[number]);
}

export function getUploadContentType(filename: string): UploadContentType | null {
    const ext = getUploadFileExtension(filename);
    if (ext === '.html' || ext === '.htm') return 'text/html';
    if (ext === '.md' || ext === '.markdown') return 'text/markdown';
    if (ext === '.png') return 'image/png';
    if (ext === '.jpg' || ext === '.jpeg') return 'image/jpeg';
    if (ext === '.webp') return 'image/webp';
    if (ext === '.gif') return 'image/gif';
    return null;
}

export function getUploadFileValidationError(filename: string, sizeBytes: number): UploadValidationError | null {
    const contentType = getUploadContentType(filename);
    if (!contentType) {
        return {
            code: 'UNSUPPORTED_FILE_TYPE',
            message: `File không hợp lệ. Chỉ hỗ trợ: ${ALLOWED_UPLOAD_EXTENSIONS.join(', ')}`,
        };
    }

    if (contentType.startsWith('image/') && sizeBytes > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
        return {
            code: 'IMAGE_TOO_LARGE',
            message: `Ảnh quá lớn. Giới hạn sau nén: ${formatUploadSize(MAX_IMAGE_UPLOAD_SIZE_BYTES)}`,
        };
    }

    if (!contentType.startsWith('image/') && sizeBytes > MAX_DOCUMENT_UPLOAD_SIZE_BYTES) {
        return {
            code: 'FILE_TOO_LARGE',
            message: `File quá lớn. Giới hạn: ${formatUploadSize(MAX_DOCUMENT_UPLOAD_SIZE_BYTES)}`,
        };
    }

    return null;
}

export function getContentTypeLabel(contentType: string): string {
    if (contentType === 'text/markdown') return 'Markdown';
    if (contentType.startsWith('image/')) return 'Image';
    return 'HTML';
}
